import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { logCollector, LogEntry } from '../components/BackendLogsViewer/BackendLogsViewer';
import { ToastService } from './ToastService';

// Track ongoing requests
const pendingRequests = new Map<string, { startTime: number; config: AxiosRequestConfig }>();

// Setup axios interceptors
export const setupApiInterceptors = () => {
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      const requestId = uuidv4();
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['X-Request-Id'] = requestId;
      
      // Store request info
      pendingRequests.set(requestId, {
        startTime: Date.now(),
        config
      });
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const requestId = response.config.headers?.['X-Request-Id'] as string;
      const requestInfo = pendingRequests.get(requestId);
      
      if (requestInfo) {
        const duration = Date.now() - requestInfo.startTime;
        const method = response.config.method?.toUpperCase() as any || 'GET';
        
        // Log the successful request
        let requestData;
        try {
          requestData = response.config.data ? 
            (typeof response.config.data === 'string' ? JSON.parse(response.config.data) : response.config.data) : 
            undefined;
        } catch (e) {
          requestData = response.config.data;
        }
        
        const logEntry: LogEntry = {
          id: requestId,
          timestamp: new Date(),
          method,
          url: response.config.url || '',
          status: response.status,
          duration,
          request: requestData,
          response: response.data,
        };
        
        logCollector.addLog(logEntry);
        pendingRequests.delete(requestId);
        
        // Show success toast for mutation operations (skip auth endpoints)
        const url = response.config.url || '';
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !url.includes('/auth/')) {
          const action = getActionName(method, url);
          ToastService.success(`${action} successful`);
        }
      }
      
      return response;
    },
    (error: AxiosError) => {
      const requestId = error.config?.headers?.['X-Request-Id'] as string;
      const requestInfo = pendingRequests.get(requestId);
      
      if (requestInfo) {
        const duration = Date.now() - requestInfo.startTime;
        const method = error.config?.method?.toUpperCase() as any || 'GET';
        
        // Log the failed request
        let requestData;
        try {
          requestData = error.config?.data ? 
            (typeof error.config.data === 'string' ? JSON.parse(error.config.data) : error.config.data) : 
            undefined;
        } catch (e) {
          requestData = error.config?.data;
        }
        
        const logEntry: LogEntry = {
          id: requestId,
          timestamp: new Date(),
          method,
          url: error.config?.url || '',
          status: error.response?.status,
          duration,
          request: requestData,
          response: error.response?.data,
          error: error.message || 'Request failed',
        };
        
        logCollector.addLog(logEntry);
        pendingRequests.delete(requestId);
        
        // Show error toast
        const action = getActionName(method, error.config?.url || '');
        const errorMessage = error.response?.data?.message || error.message || 'Request failed';
        ToastService.error(`${action} failed: ${errorMessage}`);
      }
      
      return Promise.reject(error);
    }
  );
};

// Helper function to get a user-friendly action name
function getActionName(method: string, url: string): string {
  const urlParts = url.split('/');
  const resource = urlParts[urlParts.length - 2] || urlParts[urlParts.length - 1];
  
  switch (method) {
    case 'POST':
      if (url.includes('/nodes')) return 'Node creation';
      if (url.includes('/edges')) return 'Edge creation';
      if (url.includes('/comments')) return 'Comment creation';
      if (url.includes('/mindmaps')) return 'Mindmap creation';
      return 'Creation';
      
    case 'PUT':
    case 'PATCH':
      if (url.includes('/nodes')) return 'Node update';
      if (url.includes('/edges')) return 'Edge update';
      if (url.includes('/comments')) return 'Comment update';
      if (url.includes('/mindmaps')) return 'Mindmap update';
      return 'Update';
      
    case 'DELETE':
      if (url.includes('/nodes')) return 'Node deletion';
      if (url.includes('/edges')) return 'Edge deletion';
      if (url.includes('/comments')) return 'Comment deletion';
      if (url.includes('/mindmaps')) return 'Mindmap deletion';
      return 'Deletion';
      
    default:
      return 'Operation';
  }
}
